import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomButton from "@/components/desktop/button/CustomButton";

import { useIntegrationUsage, useSyncIntegration } from "@/hooks/histories/useIOIntegration";
import useUserStore from "@/stores/useUserStore";

import styles from "./io-sync.module.scss";
import IOExternalCodes from "./IOExternalCodes";
import IOIntegrationFields from "./IOIntegrationFields";
import { INITIAL_INTEGRATION_STATE, mapApiResponseToState, mapStateToApiPayload, mapSyncResponseToState } from "./io-sync.constants";

const INTEGRATION_ID_REQUIRES_FIELDS = 6;

const formatBodyPayload = (payload) => {
	if (!payload) {
		return "";
	}

	if (typeof payload === "object") {
		return JSON.stringify(payload, null, 2);
	}

	try {
		const parsedPayload = JSON.parse(payload);
		return JSON.stringify(parsedPayload, null, 2);
	} catch {
		return String(payload);
	}
};

const IOSync = ({ isOpen, code, integrationId, balanceId, listType = null, filters = null, onClose }) => {
	const [integration, setIntegration] = useState({ ...INITIAL_INTEGRATION_STATE });
	const { user } = useUserStore();
	const isSaveMode = integration.usedExport;
	const isSuperUser = Number(user?.superuser || 0) === 1;
	const requiresIntegrationFields = Number(integrationId) === INTEGRATION_ID_REQUIRES_FIELDS;
	const modalTitle = `Detalle Sincronización`;
	const processingMessage = isSaveMode ? "Guardando código de integración..." : "Sincronizando aplicaciones de IO...";
	const primaryButtonText = isSaveMode ? "Guardar" : "Reintentar";
	const integrationResponse = String(integration.integrationResponseMessage || "")
		.split(/\r\n|\r|\n/)
		.join("\n");
	const productCode = integration.productCode || code || "Sin código";
	const bodySendPayload = formatBodyPayload(integration.bodySend);

	const queryParams = isOpen ? { code, integration: integrationId, balance: balanceId } : null;
	const { data: integrationUsageResponse, isLoading } = useIntegrationUsage(queryParams, {
		enabled: isOpen,
		onError: () => toast.error("Error al cargar los datos de la integración"),
	});

	const syncMutation = useSyncIntegration(listType, filters);

	useEffect(() => {
		if (isOpen && integrationUsageResponse?.integration) {
			setIntegration(mapApiResponseToState(integrationUsageResponse.integration));
		}
	}, [isOpen, integrationUsageResponse]);

	useEffect(() => {
		if (!isOpen) {
			setIntegration({ ...INITIAL_INTEGRATION_STATE });
		}
	}, [isOpen]);

	const handleClose = () => {
		onClose();
	};

	const handleIntegrationFieldChange = (field, value) => {
		setIntegration((previous) => ({ ...previous, [field]: value }));
	};

	const handleRetry = async () => {
		if (!integration.selectedCode && Number(integrationId) !== 2) {
			toast.warning("Debes seleccionar un código a utilizar");
			return;
		}

		try {
			const response = await syncMutation.mutateAsync({
				integration: mapStateToApiPayload(integration, { code, integrationId, balanceId }),
				list_type: listType,
				filters,
			});
			const failedIntegrations = (response?.integrations || []).filter((item) => !item.completed);
			const syncedIntegrationState = mapSyncResponseToState(response);

			if (syncedIntegrationState) {
				setIntegration((previous) => ({
					...previous,
					...syncedIntegrationState,
					productCode: syncedIntegrationState.productCode || previous.productCode,
					productName: syncedIntegrationState.productName || previous.productName,
					integrationName: syncedIntegrationState.integrationName || previous.integrationName,
					companyName: syncedIntegrationState.companyName || previous.companyName,
					agroinventarioResponseMessage:
						failedIntegrations.length > 0 ? syncedIntegrationState.agroinventarioResponseMessage || previous.agroinventarioResponseMessage : "",
					integrationResponseMessage: syncedIntegrationState.integrationResponseMessage || previous.integrationResponseMessage,
					bodySend: syncedIntegrationState.bodySend || previous.bodySend,
					externalCodes: syncedIntegrationState.externalCodes.length > 0 ? syncedIntegrationState.externalCodes : previous.externalCodes,
					selectedCode: syncedIntegrationState.selectedCode || previous.selectedCode,
				}));
			}

			if (failedIntegrations.length > 0) {
				toast.error("Ha ocurrido un error al sincronizar las aplicaciones, por favor intente nuevamente");
			} else if (isSaveMode) {
				toast.success("Se ha guardado correctamente el código");
			} else {
				toast.success("Se ha sincronizado correctamente las aplicaciones");
				setTimeout(() => handleClose(), 1000);
			}
		} catch {
			toast.error("Error al sincronizar las aplicaciones");
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<CustomModalForm
			open={isOpen}
			title={modalTitle}
			onClose={handleClose}
			showSave={false}
			size="lg"
			gridTable={false}
			isLoading={syncMutation.isLoading}
			processingMessage={processingMessage}
			footer={
				<>
					<CustomButton variant="secondary" onClick={handleClose} disabled={syncMutation.isLoading}>
						Cerrar
					</CustomButton>
					<CustomButton variant="primary" onClick={handleRetry} loading={syncMutation.isLoading}>
						{primaryButtonText}
					</CustomButton>
				</>
			}
		>
			{isLoading ? (
				<CustomFetching center message="Cargando datos de integración..." />
			) : (
				<div className={styles.container}>
					<section className={styles.heroCard}>
						<div className={styles.heroBadge}>Sincronización IO</div>
						<h3 className={styles.heroTitle}>{integration.productName || "Producto sin nombre"}</h3>
						<div className={styles.heroMetaGrid}>
							<div className={styles.heroMetaItem}>
								<span className={styles.heroMetaLabel}>Código Salida</span>
								<strong className={styles.heroMetaValue}>{productCode}</strong>
							</div>
							<div className={styles.heroMetaItem}>
								<span className={styles.heroMetaLabel}>Integración</span>
								<strong className={styles.heroMetaValue}>{integration.integrationName || "No definida"}</strong>
							</div>
							<div className={styles.heroMetaItem}>
								<span className={styles.heroMetaLabel}>Empresa</span>
								<strong className={styles.heroMetaValue}>{integration.companyName || "No definida"}</strong>
							</div>
						</div>
					</section>

					{!!integration.agroinventarioResponseMessage && (
						<section className={styles.responseCard}>
							<p className={styles.responseTitle}>Respuesta Agroinventario</p>
							<p className={styles.responseText}>{integration.agroinventarioResponseMessage}</p>
						</section>
					)}

					{!!integration.integrationResponseMessage && (
						<section className={styles.responseCard}>
							<p className={styles.responseTitle}>Respuesta {integration.integrationName}</p>
							<p className={styles.responseText}>{integrationResponse}</p>
						</section>
					)}

					{isSuperUser && !!bodySendPayload && (
						<section className={styles.payloadCard}>
							<p className={styles.payloadTitle}>Body Send (solo superusuario)</p>
							<pre className={styles.payloadContent}>{bodySendPayload}</pre>
						</section>
					)}

					<section className={styles.codesCard}>
						<p className={styles.codesLabel}>
							Códigos {integration.integrationName} - {integration.companyName}
						</p>
						<IOExternalCodes
							externalCodes={integration.externalCodes}
							selectedCode={integration.selectedCode}
							inventoryId={integration.inventoryId}
							listType={listType}
							filters={filters}
							onExternalCodesChange={(newCodes) => handleIntegrationFieldChange("externalCodes", newCodes)}
							onSelectedCodeChange={(selectedExternalCode) => handleIntegrationFieldChange("selectedCode", selectedExternalCode)}
						/>
					</section>

					{requiresIntegrationFields && (
						<section className={styles.fieldsCard}>
							<IOIntegrationFields
								requiresBatch={integration.requiresBatch}
								productionBatch={integration.productionBatch}
								expirationDate={integration.expirationDate}
								onChange={handleIntegrationFieldChange}
							/>
						</section>
					)}
				</div>
			)}
		</CustomModalForm>
	);
};

export default IOSync;
